/**
 * SESSIONS AND REVOCATIONS → HEBREW. `packages/authn/src/session-secret.ts`, `packages/connector`.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "REVOKED" IS SEVEN DIFFERENT FACTS, AND TWO OF THEM ARE ABOUT AN ATTACK.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `REVOCATION_REASONS` has seven members and they are not seven shades of the same event. Five are
 * things the SYSTEM observed — a password changed, a recovery code was used, a membership was
 * disabled — and two are things a PERSON asserted. The service enforces that split: an administrator
 * may assert only `administrator_revoked` and `suspected_compromise`, and anything else is refused
 * with the permitted pair as the `excess`, *because an administrator who could assert
 * `password_changed` would be writing a password change that never happened into the audit trail.*
 *
 * A single sentence for all seven would destroy the only thing the list is read for: whether the
 * session ended because of something ordinary or because somebody believed an account was in the
 * wrong hands.
 *
 * ── THE SAME SPLIT, ONE LAYER DOWN: CONNECTOR CREDENTIALS ───────────────────────────────────────
 *
 * `CREDENTIAL_REVOCATION_REASONS` divides the same way, and `RETROSPECTIVE_REVOCATION_REASONS` names
 * the half that matters: *"`suspected_compromise` means every message that credential ever signed is
 * now in question, and `connector_decommissioned` means none of them are."* That is not a nuance —
 * it is the difference between an office that must re-examine a month of postings and one that must
 * do nothing at all, and one amber "credential revoked" chip owes them both the same silence.
 *
 * ── WHAT A SESSION ROW MAY SAY ABOUT A PERSON ───────────────────────────────────────────────────
 *
 * How many, since when, and whether it can be evicted. No IP address, no device fingerprint, no
 * user-agent string — because `tenant.user.session.read` is an administrator reading about A
 * COLLEAGUE, is audited ALWAYS for exactly that reason, and "where does this person work from" is
 * not a question the product asked to be able to answer.
 *
 * Pure. Every fact arrives already established. Nothing here revokes, decides, or infers a state.
 */

import type {
  CredentialRevocationReason,
  SessionRevocationReason,
  UserSessionSummary,
} from './contract';
import {
  ADMIN_ASSERTABLE_REVOCATION_REASONS,
  RETROSPECTIVE_REVOCATION_REASONS,
} from './contract';
import type { Tone } from './adminVocabulary';
import { formatInstantHe } from './adminVocabulary';

// ── Session revocation ────────────────────────────────────────────────────────────────────────────

export interface RevocationTermHe {
  readonly labelHe: string;
  /** One sentence saying what the office should conclude. Different per reason, deliberately. */
  readonly meaningHe: string;
  readonly tone: Tone;
  /** `true` ⇒ the system did this on its own; nobody asserted it. */
  readonly automatic: boolean;
}

const SESSION_REVOCATION_HE: Readonly<Record<SessionRevocationReason, RevocationTermHe>> = {
  user_signed_out: {
    labelHe: 'המשתמש התנתק',
    meaningHe: 'ההתחברות הסתיימה כרגיל, ביוזמת המשתמש עצמו.',
    tone: 'muted',
    automatic: false,
  },
  password_changed: {
    labelHe: 'הסיסמה הוחלפה',
    meaningHe: 'החלפת סיסמה מסיימת כל התחברות קיימת, כדי שההחלפה תתפוס גם במכשירים אחרים.',
    tone: 'muted',
    automatic: true,
  },
  mfa_changed: {
    labelHe: 'האימות הדו-שלבי שונה',
    meaningHe: 'שינוי הגורם השני מסיים כל התחברות שנפתחה עם הגורם הקודם.',
    tone: 'muted',
    automatic: true,
  },
  recovery_code_used: {
    labelHe: 'נעשה שימוש בקוד שחזור',
    meaningHe:
      'קוד שחזור הוא מסלול חירום חד-פעמי, ולכן ההתחברויות הקודמות נסגרות. ' +
      'אם המשתמש לא ביקש זאת — זהו סימן שיש לבדוק.',
    tone: 'warn',
    automatic: true,
  },
  membership_disabled: {
    labelHe: 'המשתמש הושבת בארגון',
    meaningHe: 'השבתת משתמש מסיימת מיד את ההתחברויות שלו. הרשומה וההיסטוריה שלו נשמרות.',
    tone: 'muted',
    automatic: true,
  },
  tenant_terminated: {
    labelHe: 'הארגון נסגר',
    // NOT `administrator_revoked`. That names one person acting on one account; this names the whole
    // office ending, and an office reading its own log must be able to tell the two apart.
    meaningHe:
      'ההתחברות הסתיימה משום שההתקשרות עם הארגון הסתיימה, ולא בשל החלטה על המשתמש הזה. ' +
      'הנתונים אינם נמחקים, וההתחברות פשוט אינה נפתחת מחדש.',
    tone: 'muted',
    automatic: false,
  },
  administrator_revoked: {
    labelHe: 'מנהל ניתק את ההתחברות',
    // A fact about a DEVICE, not about the account. The distinction is the service's own.
    meaningHe:
      'מנהל בארגון ניתק את ההתחברות הזו — למשל מכשיר שנשאר מחובר במקום שאין אליו גישה יותר. ' +
      'אין בכך קביעה שמשהו נפרץ.',
    tone: 'warn',
    automatic: false,
  },
  suspected_compromise: {
    labelHe: 'חשד שההתחברות הגיעה לידיים לא נכונות',
    meaningHe:
      'ההתחברות נותקה מתוך חשד. זו קביעה שמישהו רשם במפורש, והיא מחייבת בדיקה של מה שנעשה ' +
      'בהתחברות הזו לפני שנותקה.',
    tone: 'error',
    automatic: false,
  },
};

export function sessionRevocationHe(reason: SessionRevocationReason): RevocationTermHe {
  return SESSION_REVOCATION_HE[reason];
}

/** Exposed for the mirror test only. */
export function mappedSessionRevocationReasons(): readonly string[] {
  return Object.keys(SESSION_REVOCATION_HE);
}

/**
 * The two an administrator may choose, as a control's options.
 *
 * Derived from the contract's own list rather than re-typed, so a change on the server side is a
 * compile error here and not a silently stale radio group.
 */
export interface RevocationChoice {
  readonly reason: SessionRevocationReason;
  readonly labelHe: string;
  readonly hintHe: string;
}

export function adminRevocationChoices(): readonly RevocationChoice[] {
  return ADMIN_ASSERTABLE_REVOCATION_REASONS.map((reason) => ({
    reason,
    labelHe: SESSION_REVOCATION_HE[reason].labelHe,
    hintHe: SESSION_REVOCATION_HE[reason].meaningHe,
  }));
}

/**
 * The refusal when a caller asserted a reason that is not theirs to assert.
 *
 * It arrives as a conflict carrying the PERMITTED pair as `excess` — the same field name a delegation
 * ceiling uses, and the same idea: name what was refused rather than that something was.
 */
export function revocationReasonRefusalText(permitted: readonly string[]): {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
} {
  const named = permitted
    .filter((r): r is SessionRevocationReason => r in SESSION_REVOCATION_HE)
    .map((r) => SESSION_REVOCATION_HE[r].labelHe);
  return {
    titleHe: 'הסיבה שנבחרה אינה סיבה שמנהל רשאי לקבוע',
    explanationHe:
      'חלק מסיבות הניתוק הן עובדות שהמערכת מתעדת בעצמה — כמו החלפת סיסמה — ומנהל אינו יכול לרשום ' +
      'אותן, כדי שביומן לא יירשם אירוע שלא קרה. לא נותקה אף התחברות.',
    nextStepHe:
      named.length === 0
        ? 'יש לבחור סיבה מתוך האפשרויות שהמסך מציע.'
        : `הסיבות שמנהל רשאי לקבוע: ${named.join(' · ')}.`,
  };
}

// ── A session row ─────────────────────────────────────────────────────────────────────────────────

export interface SessionRowText {
  readonly startedHe: string;
  /** `null` ⇒ never seen since it started. Written as an absence, never as the start time again. */
  readonly lastSeenHe: string | null;
  readonly expiresHe: string | null;
  /** The written state of the row: live, this one, or ended with a reason. */
  readonly stateHe: string;
  readonly tone: Tone;
  /** `false` for a session that has already ended — there is nothing left to revoke. */
  readonly revocable: boolean;
}

export function sessionRowText(session: UserSessionSummary): SessionRowText {
  const started = formatInstantHe(session.startedAtUtc);
  const lastSeen = formatInstantHe(session.lastSeenAtUtc);
  const expires = formatInstantHe(session.expiresAtUtc);

  if (session.revokedReason !== null) {
    const term = SESSION_REVOCATION_HE[session.revokedReason];
    return {
      startedHe: started === null ? 'מועד הפתיחה אינו ידוע' : `נפתחה ב־${started}`,
      lastSeenHe: lastSeen === null ? null : `פעילות אחרונה ב־${lastSeen}`,
      expiresHe: null,
      // 02 §8: the row stays, with the reason. A revoked session that vanished would take the one
      // piece of evidence an office needs in order to ask what happened during it.
      stateHe: `הסתיימה — ${term.labelHe}`,
      tone: term.tone,
      revocable: false,
    };
  }

  return {
    startedHe: started === null ? 'מועד הפתיחה אינו ידוע' : `נפתחה ב־${started}`,
    lastSeenHe: lastSeen === null ? 'טרם נרשמה פעילות מאז הפתיחה' : `פעילות אחרונה ב־${lastSeen}`,
    expiresHe: expires === null ? null : `תפוג ב־${expires}`,
    stateHe: session.current ? 'ההתחברות הנוכחית — המכשיר שאתה משתמש בו כרגע' : 'התחברות פעילה',
    tone: session.current ? 'ok' : 'warn',
    revocable: true,
  };
}

/** 09 §5.1's "313 is not 313": the headline says which N it is. */
export function sessionSummaryHe(sessions: readonly UserSessionSummary[]): string {
  const live = sessions.filter((s) => s.revokedReason === null).length;
  const ended = sessions.length - live;
  return ended === 0
    ? `${live} התחברויות פעילות`
    : `${live} התחברויות פעילות · ${ended} שהסתיימו ונשמרו ביומן`;
}

/**
 * "SIGN OUT EVERYWHERE" — including the browser saying it.
 *
 * The route clears the caller's own cookies, and that is the point rather than an oversight: this is
 * reached by somebody who believes a session of theirs is in the wrong hands, and sparing the one
 * they are using would leave alive the browser they are least sure about. The blast radius therefore
 * has to say it out loud, before the click.
 */
export const SIGN_OUT_EVERYWHERE_HE = {
  intentHe: 'ניתוק כל ההתחברויות',
  blastRadiusHe:
    'כל ההתחברויות של החשבון שלך יסתיימו, כולל זו שאתה משתמש בה עכשיו — תידרש התחברות מחדש. ' +
    'שום נתון אינו נמחק, ואף הרשאה אינה משתנה.',
  confirmLabelHe: 'ניתוק כל ההתחברויות',
} as const;

// ── Connector credentials ─────────────────────────────────────────────────────────────────────────

export interface CredentialRevocationTextHe {
  readonly labelHe: string;
  readonly meaningHe: string;
  readonly tone: Tone;
  /**
   * `true` ⇒ every message that credential ever signed is now in question, and the office has work to
   * do about the PAST. `false` ⇒ only future traffic stops, and there is nothing to re-examine.
   */
  readonly retrospective: boolean;
}

const CREDENTIAL_REVOCATION_HE: Readonly<Record<CredentialRevocationReason, string>> = {
  suspected_compromise: 'עלה חשד שפרטי ההזדהות של החיבור הגיעו לגורם אחר.',
  operator_revoked: 'פרטי ההזדהות בוטלו ביוזמת מפעיל המערכת.',
  connector_decommissioned: 'תוכנת החיבור הוצאה משימוש, ופרטי ההזדהות שלה בוטלו יחד איתה.',
  tenant_closed: 'הלקוח נסגר, ולכן פרטי ההזדהות של החיבורים שלו בוטלו.',
  enrolment_disputed: 'עלתה טענה שרישום תוכנת החיבור לא נעשה בידי מי שנרשם.',
  rotation_forced: 'פרטי ההזדהות הוחלפו במסגרת החלפה תקופתית מתוכננת.',
};

/** Exposed for the mirror test only. */
export function mappedCredentialRevocationReasons(): readonly string[] {
  return Object.keys(CREDENTIAL_REVOCATION_HE);
}

export function credentialRevocationText(
  reason: CredentialRevocationReason,
): CredentialRevocationTextHe {
  const retrospective = RETROSPECTIVE_REVOCATION_REASONS.includes(reason);
  return {
    labelHe: 'פרטי ההזדהות של החיבור בוטלו',
    meaningHe:
      CREDENTIAL_REVOCATION_HE[reason] +
      (retrospective
        ? ' יש לבחון גם נתונים שנקלטו דרך החיבור הזה בעבר, ולא רק את הפסקת הקליטה.'
        : ' נתונים שנקלטו בעבר דרך החיבור הזה אינם מוטלים בספק — רק הקליטה מכאן ואילך נעצרה.'),
    tone: retrospective ? 'error' : 'muted',
    retrospective,
  };
}
